// REFACTORED: 2026-02-06
// FROM src/components/MyFilesPage.tsx

import React, { useState, useEffect, useMemo } from 'react';
import { SectionSuspenseSkeleton, EmptyState } from '../components/common/loading';

import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Card,
  CardContent,
  CardActions,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  List,
  ListItem,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  OpenInNew as OpenInNewIcon,
  Link as LinkIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import SelectAllMultiSelect from '../components/common/SelectAllMultiSelect';
import { useQuery } from '@tanstack/react-query';
import { getMyFiles } from '../services/api';
import type { MyFilesFilterRequest, MyFileItem } from '../types/api';

type ViewMode = 'grid' | 'list';
type ScopeMode = 'all' | 'org' | 'my-bu-sbu';
type CardSize = 'small' | 'medium' | 'large';

const MyFilesPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [scopeMode, setScopeMode] = useState<ScopeMode>('all');
  const [cardSize, setCardSize] = useState<CardSize>('medium');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filters, setFilters] = useState<MyFilesFilterRequest>({
    access_level: ['Org'],
  });

  // Update filters when scope changes (category and search are frontend-only)
  useEffect(() => {
    const newFilters: MyFilesFilterRequest = {
    };
    if (scopeMode === 'all') {
      newFilters.access_level = ['Org', 'BU', 'SBU'];
    } 
    else if (scopeMode === 'org') {
      newFilters.access_level = ['Org'];
    } 
    else {
      newFilters.access_level = ['BU', 'SBU'];
    }

    setFilters(newFilters);
  }, [scopeMode]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['my-files', filters],
    queryFn: () => getMyFiles(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const allFiles = data?.files || [];
  const categories = data?.categories || [];

  // Frontend-only filtering for search and category
  const files = useMemo(() => {
    let filtered = allFiles;

    // Filter by category (multiple selection)
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((file) => 
        selectedCategories.includes(file.document_category)
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((file) => {
        const titleMatch = file.title.toLowerCase().includes(query);
        const descriptionMatch = file.description?.toLowerCase().includes(query) || false;
        const tagsMatch = file.tags.some(tag => tag.toLowerCase().includes(query));
        return titleMatch || descriptionMatch || tagsMatch;
      });
    }

    return filtered;
  }, [allFiles, selectedCategories, searchQuery]);

  const getFileIcon = (file: MyFileItem) => {
    if (file.drive_url.includes('drive.google.com')) {
      if (file.drive_url.includes('.pdf')) return <PdfIcon />;
      return <FileIcon />;
    }
    return <LinkIcon />;
  };

  const handleOpenResource = (driveUrl: string) => {
    window.open(driveUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCategoryChange = (categories: string[]) => {
    setSelectedCategories(categories);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const categoryOptions = useMemo(
    () => categories.map((cat) => ({ value: cat, label: cat })),
    [categories]
  );

  // Get card styling based on size
  const getCardStyles = () => {
    switch (cardSize) {
      case 'small':
        return {
          padding: 1,
          titleVariant: 'body2' as const,
          titleFontSize: '0.875rem',
          descriptionVariant: 'caption' as const,
          descriptionFontSize: '0.7rem',
          minHeight: 30,
          spacing: 0.5,
          iconSize: '0.6rem',
          chipSize: '0.55rem',
          buttonSize: '0.7rem',
          borderRadius: '20px',
        };
      case 'medium':
        return {
          padding: 1.5,
          titleVariant: 'body2' as const,
          titleFontSize: '0.9rem',
          descriptionVariant: 'body2' as const,
          descriptionFontSize: '0.75rem',
          minHeight: 40,
          spacing: 1,
          iconSize: '0.7rem',
          chipSize: '0.65rem',
          buttonSize: '0.8rem',
          borderRadius: '20px',
        };
      case 'large':
      default:
        return {
          padding: 2.5,
          titleVariant: 'body2' as const,
          titleFontSize: '1rem',
          descriptionVariant: 'body2' as const,
          descriptionFontSize: '0.8rem',
          minHeight: 48,
          spacing: 2,
          iconSize: '0.75rem',
          chipSize: '0.7rem',
          buttonSize: '0.875rem',
          borderRadius: '20px',
        };
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load files. Please try again later.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 4, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={scopeMode}
          exclusive
          onChange={(_, newValue) => {
            if (newValue !== null) setScopeMode(newValue);
          }}
          size="small"
        >
          <ToggleButton value="all" sx={{ px: 3 }}>All</ToggleButton>
          <ToggleButton value="org" sx={{ px: 1 }}>Org Wide</ToggleButton>
          <ToggleButton value="my-bu-sbu" sx={{ px: 1 }}>My BU/SBU</ToggleButton>
        </ToggleButtonGroup>

        <SelectAllMultiSelect
          label="Categories"
          options={categoryOptions}
          selectedValues={selectedCategories}
          onChange={handleCategoryChange}
          allLabel="All"
          fullWidth={false}
          sx={{ minWidth: 200, maxWidth: 300 }}
        />

        <TextField
          placeholder="Search resources..."
          value={searchQuery}
          onChange={handleSearchChange}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250, flexGrow: 1, maxWidth: 400 }}
        />

        <ToggleButtonGroup
          value={cardSize}
          exclusive
          onChange={(_, newValue) => {
            if (newValue !== null) setCardSize(newValue);
          }}
          size="small"
        >
          <ToggleButton value="small">Small</ToggleButton>
          <ToggleButton value="medium">Medium</ToggleButton>
          <ToggleButton value="large">Large</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newValue) => {
            if (newValue !== null) setViewMode(newValue);
          }}
          size="small"
        >
          <ToggleButton value="grid"><GridViewIcon /></ToggleButton>
          <ToggleButton value="list"><ViewListIcon /></ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isLoading && (
        <SectionSuspenseSkeleton message="Loading files…" minHeight={240} py={4} />
      )}

      {!isLoading && files.length === 0 && (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <EmptyState title="No files found" description="Try changing scope or search." />
        </Paper>
      )}

      {!isLoading && files.length > 0 && viewMode === 'grid' && (() => {
        const cardStyles = getCardStyles();
        const getGridSize = () => {
          switch (cardSize) {
            case 'small': return { xs: 12, sm: 6, md: 2 };
            case 'medium': return { xs: 12, sm: 6, md: 3 };
            case 'large': default: return { xs: 12, sm: 6, md: 4 };
          }
        };
        const gridSize = getGridSize();
        return (
          <Grid container spacing={3}>
            {files.map((file) => (
              <Grid item {...gridSize} key={file.document_id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s, box-shadow 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }, borderRadius: '15px' }}>
                  <CardContent sx={{ flexGrow: 1, p: cardStyles.padding }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', mb: cardStyles.spacing }}>
                      <Chip label={file.document_access_level} size="small" color={file.document_access_level === 'Org' ? 'primary' : 'secondary'} sx={{ fontSize: cardStyles.chipSize }} />
                    </Box>
                    <Typography variant={cardStyles.titleVariant} component="h3" sx={{ mb: 0.5, fontWeight: 600, fontSize: cardStyles.titleFontSize }}>{file.title}</Typography>
                    <Tooltip title={file.description || 'No description available.'} arrow>
                      <Typography variant={cardStyles.descriptionVariant} color="text.secondary" sx={{ mb: cardStyles.spacing, minHeight: cardStyles.minHeight, fontSize: cardStyles.descriptionFontSize, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {file.description || 'No description available.'}
                      </Typography>
                    </Tooltip>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                      {file.tags.slice(0, cardSize === 'small' ? 2 : 3).map((tag, index) => (
                        <Chip key={index} label={`#${tag}`} size="small" variant="outlined" sx={{ fontSize: cardStyles.chipSize }} />
                      ))}
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: cardStyles.padding, pt: 0 }}>
                    <Button variant="contained" size="small" endIcon={<OpenInNewIcon />} onClick={() => handleOpenResource(file.drive_url)} fullWidth sx={{ fontSize: cardStyles.buttonSize, borderRadius: '15px' }}>Open Resource</Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        );
      })()}

      {!isLoading && files.length > 0 && viewMode === 'list' && (() => {
        const cardStyles = getCardStyles();
        return (
          <Paper>
            <List>
              {files.map((file, index) => (
                <React.Fragment key={file.document_id}>
                  <ListItem sx={{ py: cardStyles.padding, '&:hover': { backgroundColor: 'action.hover' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: cardStyles.spacing, width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 50 }}>{getFileIcon(file)}</Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography variant={cardStyles.titleVariant} component="h3" sx={{ fontWeight: 600, fontSize: cardStyles.titleFontSize }}>{file.title}</Typography>
                          <Chip label={file.document_access_level} size="small" color={file.document_access_level === 'Org' ? 'primary' : 'secondary'} sx={{ fontSize: cardStyles.chipSize }} />
                        </Box>
                        <Typography variant={cardStyles.descriptionVariant} color="text.secondary" sx={{ mb: 0.5, fontSize: cardStyles.descriptionFontSize, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.description || 'No description available.'}</Typography>
                      </Box>
                      <Button variant="contained" size="small" endIcon={<OpenInNewIcon />} onClick={() => handleOpenResource(file.drive_url)} sx={{ fontSize: cardStyles.buttonSize }}>Open</Button>
                    </Box>
                  </ListItem>
                  {index < files.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        );
      })()}

      {!isLoading && files.length > 0 && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">Showing {files.length} of {data?.total_count || 0} resources</Typography>
        </Box>
      )}
    </Box>
  );
};

export default MyFilesPage;
